import { apiClient } from './client';
import type {
  Cliente,
  ClienteCompromisosResponse,
  Compromiso,
  EntidadEvento,
  FacilityWithPropuestas,
  Hito,
  HitoEstado,
  HitoEvento,
  HitoParentType,
  PropuestaArchivados,
  PropuestaDetail,
  Tag,
} from '@/types';

export async function listClientes(): Promise<Cliente[]> {
  const { data } = await apiClient.get<Cliente[]>('/clientes');
  return data;
}

export async function getCliente(slug: string): Promise<Cliente> {
  const { data } = await apiClient.get<Cliente>(`/clientes/${slug}`);
  return data;
}

/** R5: vista consolidada de compromisos del cliente con contexto y flags. */
export async function getClienteCompromisos(
  slug: string,
): Promise<ClienteCompromisosResponse> {
  const { data } = await apiClient.get<ClienteCompromisosResponse>(
    `/clientes/${slug}/compromisos`,
  );
  return data;
}

/** R2: ahora devuelve la lista de propuestas de la facility, no el detalle completo. */
export async function getFacilityWithPropuestas(
  clienteSlug: string,
  facilitySlug: string,
): Promise<FacilityWithPropuestas> {
  const { data } = await apiClient.get<FacilityWithPropuestas>(
    `/clientes/${clienteSlug}/facilities/${facilitySlug}`,
  );
  return data;
}

/** R2: detalle completo de una propuesta. */
export async function getPropuestaDetail(propuestaId: string): Promise<PropuestaDetail> {
  const { data } = await apiClient.get<PropuestaDetail>(`/propuestas/${propuestaId}`);
  return data;
}

/**
 * Cambia estado de un hito (o flags). En R3, todo cambio de estado requiere
 * `comentario`; `evidenciaUrl` es opcional.
 */
export async function updateHito(
  hitoId: string,
  patch: {
    done?: boolean;
    isFormal?: boolean;
    isClave?: boolean;
    estado?: HitoEstado;
    comentario?: string;
    evidenciaUrl?: string;
  },
) {
  const { data } = await apiClient.put(`/hitos/${hitoId}`, patch);
  return data;
}

/** Historial de control de cambios de un hito. */
export async function listEventos(hitoId: string): Promise<HitoEvento[]> {
  const { data } = await apiClient.get<HitoEvento[]>(`/hitos/${hitoId}/eventos`);
  return data;
}

export async function listTags(): Promise<Tag[]> {
  const { data } = await apiClient.get<Tag[]>('/tags');
  return data;
}

export async function assignTag(compromisoId: string, tagId: string) {
  const { data } = await apiClient.post(`/compromisos/${compromisoId}/tags/${tagId}`);
  return data;
}

export async function unassignTag(compromisoId: string, tagId: string) {
  const { data } = await apiClient.delete(`/compromisos/${compromisoId}/tags/${tagId}`);
  return data;
}

/** Crear un tag nuevo (inline desde el drawer de edición). */
export async function createTag(input: {
  categoria: string;
  nombre: string;
  color?: string;
  slug?: string;
}): Promise<Tag> {
  const { data } = await apiClient.post<Tag>('/tags', input);
  return data;
}

// ── R5+ — CRUD de compromisos y items ──────────────────────────────────────

export interface UpsertCompromisoPayload {
  codigo?: string;
  nombre?: string;
  descripcion?: string | null;
  iniciativaId?: string;
  tagIds?: string[];
}

export interface CreateCompromisoPayload extends UpsertCompromisoPayload {
  codigo: string;
  nombre: string;
  iniciativaId: string;
  items?: { titulo: string }[];
}

export async function createCompromiso(payload: CreateCompromisoPayload): Promise<Compromiso> {
  const { data } = await apiClient.post<Compromiso>('/compromisos', payload);
  return data;
}

export async function updateCompromiso(
  id: string,
  payload: UpsertCompromisoPayload,
): Promise<Compromiso> {
  const { data } = await apiClient.patch<Compromiso>(`/compromisos/${id}`, payload);
  return data;
}

export async function archiveCompromiso(id: string) {
  const { data } = await apiClient.delete(`/compromisos/${id}`);
  return data;
}

export async function restoreCompromiso(id: string) {
  const { data } = await apiClient.post(`/compromisos/${id}/restore`);
  return data;
}

export async function listCompromisoEventos(id: string): Promise<EntidadEvento[]> {
  const { data } = await apiClient.get<EntidadEvento[]>(`/compromisos/${id}/eventos`);
  return data;
}

// ── Items (hitos) ──────────────────────────────────────────────────────────

export interface CreateHitoPayload {
  parentType: HitoParentType;
  parentId: string;
  titulo: string;
  descripcion?: string;
  isFormal?: boolean;
  isClave?: boolean;
}

export async function createHito(payload: CreateHitoPayload): Promise<Hito> {
  const { data } = await apiClient.post<Hito>('/hitos', payload);
  return data;
}

export async function editHito(
  id: string,
  payload: { titulo?: string; descripcion?: string | null },
): Promise<Hito> {
  const { data } = await apiClient.patch<Hito>(`/hitos/${id}`, payload);
  return data;
}

export async function archiveHito(id: string) {
  const { data } = await apiClient.delete(`/hitos/${id}`);
  return data;
}

export async function restoreHito(id: string) {
  const { data } = await apiClient.post(`/hitos/${id}/restore`);
  return data;
}

// ── Archivados ─────────────────────────────────────────────────────────────

export async function listArchivados(propuestaId: string): Promise<PropuestaArchivados> {
  const { data } = await apiClient.get<PropuestaArchivados>(
    `/propuestas/${propuestaId}/archivados`,
  );
  return data;
}
